/** @typedef {[number, number]} Point */
/** @typedef {{ x: number; y: number }} Vec2 */
/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */
/** @typedef {import('./floorPlanDesignerState.js').DesignerWall} DesignerWall */
/** @typedef {import('./floorPlanDesignerState.js').DesignerPoint} DesignerPoint */
/** @typedef {import('./floorPlanDesignerRoomBoundaries.js').DetectedRoom} DetectedRoom */

import {
  DEFAULT_WALL_THICKNESS_M,
  MIN_WALL_LENGTH_METERS,
  getPointById,
} from './floorPlanDesignerState.js';
import {
  directedEdgeKey,
  getPartitionWallIds,
  signedPolygonArea2D,
} from './floorPlanDesignerGeometry.js';
import { getRoomIdForDetectedRoom } from './floorPlanDesignerRooms.js';
import { buildRoomBoundaryModel } from './floorPlanDesignerRoomBoundaries.js';
import {
  buildBoundaryWallBundle,
  getTJunctionAtPivot,
} from './floorPlanDesignerBoundaryWalls.js';
import {
  arePolygonPointsFinite,
  isPolygonSelfIntersecting,
  polygonArea,
} from './floorPlanDesignerWallRectangles.js';
import {
  buildDynamicWallRenderModel,
  buildWallBodySegmentPolygon,
} from './floorPlanDesignerDynamicWallEnds.js';

export const DEFAULT_WALL_THICKNESS_DIRECTION = 'outside';

const MIN_POLYGON_AREA = 1e-2;
const PARALLEL_EPS = 1e-8;

/**
 * @typedef {{
 *   thicknessMeters: number;
 *   thicknessDirection: 'inside' | 'outside';
 * }} WallSettings
 */

/**
 * @typedef {{
 *   points: DesignerPoint[];
 *   walls: DesignerWall[];
 *   wallSettings: WallSettings;
 * }} DesignerGraph
 */

/**
 * @param {DesignerState | DesignerGraph} input
 * @returns {DesignerGraph}
 */
export function toDesignerGraph(input) {
  return {
    points: input.points,
    walls: input.walls,
    wallSettings: normalizeWallSettings(
      'wallSettings' in input ? input.wallSettings : null,
    ),
  };
}

/**
 * @param {Partial<WallSettings> | null | undefined} settings
 * @returns {WallSettings}
 */
export function normalizeWallSettings(settings) {
  const thicknessMeters =
    settings?.thicknessMeters != null && Number.isFinite(settings.thicknessMeters)
      ? settings.thicknessMeters
      : DEFAULT_WALL_THICKNESS_M;
  const thicknessDirection =
    settings?.thicknessDirection === 'inside' ? 'inside' : DEFAULT_WALL_THICKNESS_DIRECTION;
  return { thicknessMeters, thicknessDirection };
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {{ dir: Vec2; left: Vec2; right: Vec2; length: number }}
 */
export function wallAxisFromPivotEdge(start, end) {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) {
    return { dir: { x: 1, y: 0 }, left: { x: 0, y: 1 }, right: { x: 0, y: -1 }, length: 0 };
  }
  const dir = { x: dx / length, y: dy / length };
  return {
    dir,
    left: { x: -dir.y, y: dir.x },
    right: { x: dir.y, y: -dir.x },
    length,
  };
}

/**
 * @param {Vec2} v
 * @returns {Vec2}
 */
function negateVec(v) {
  return { x: -v.x, y: -v.y };
}

/**
 * Fixed left normal from wall direction — physical walls ignore room context.
 * @param {Point} start
 * @param {Point} end
 * @returns {Vec2}
 */
export function getCenterlineWallNormal(start, end) {
  const { left } = wallAxisFromPivotEdge(start, end);
  return left;
}

/**
 * @typedef {{ outer: Point; inner: Point }} JoinPair
 * @exports JoinPair
 */

/**
 * @typedef {{ wallId: string; fromPointId: string; toPointId: string }} BoundaryEdgeRef
 */

/**
 * @param {string} pivotId
 * @param {string} wallId
 * @returns {string}
 */
export function wallPivotJoinKey(pivotId, wallId) {
  return `${pivotId}|${wallId}`;
}

/**
 * @param {string} pivotId
 * @param {string} wallId
 * @param {Map<string, JoinPair | null>} joinPoints
 * @param {Map<string, JoinPair>} roomJoinPoints
 * @param {Map<string, JoinPair>} [wallPivotJoins]
 * @param {string | null} [roomId]
 * @returns {JoinPair | null}
 */
export function getJoinPairForWallAtPivot(
  pivotId,
  wallId,
  joinPoints,
  roomJoinPoints,
  wallPivotJoins,
  roomId = null,
) {
  if (wallPivotJoins) {
    const perWall = wallPivotJoins.get(wallPivotJoinKey(pivotId, wallId));
    if (perWall) return perWall;
  }
  return getJoinPairForPivot(pivotId, joinPoints, roomJoinPoints, roomId);
}

/**
 * @param {string} pivotId
 * @param {Map<string, JoinPair | null>} joinPoints
 * @param {Map<string, JoinPair>} roomJoinPoints
 * @param {string | null} [roomId]
 * @returns {JoinPair | null}
 */
function getJoinPairForPivot(pivotId, joinPoints, roomJoinPoints, roomId = null) {
  const global = joinPoints.get(pivotId);
  if (global) return global;
  if (roomId) {
    const roomJoin = roomJoinPoints.get(`${pivotId}:${roomId}`);
    if (roomJoin) return roomJoin;
  }
  for (const [key, value] of roomJoinPoints.entries()) {
    if (key.startsWith(`${pivotId}:`)) return value;
  }
  return null;
}

/**
 * @param {DesignerGraph} graph
 * @returns {Map<string, Array<{ wallId: string; otherPointId: string }>>}
 */
export function buildPivotAdjacency(graph) {
  /** @type {Map<string, Array<{ wallId: string; otherPointId: string }>>} */
  const adj = new Map();
  for (const wall of graph.walls) {
    if (!adj.has(wall.startPointId)) adj.set(wall.startPointId, []);
    if (!adj.has(wall.endPointId)) adj.set(wall.endPointId, []);
    adj.get(wall.startPointId).push({ wallId: wall.id, otherPointId: wall.endPointId });
    adj.get(wall.endPointId).push({ wallId: wall.id, otherPointId: wall.startPointId });
  }
  return adj;
}

/**
 * @typedef {{
 *   roomId: string;
 *   signedArea: number;
 *   thicknessDirection: 'inside' | 'outside';
 *   edgeFollowsLoop: boolean;
 * }} RoomEdgeContext
 */

/**
 * @param {DesignerWall} wall
 * @param {Map<string, RoomEdgeContext>} edgeContextMap
 * @returns {RoomEdgeContext | null}
 */
function resolveWallDirectedContext(wall, edgeContextMap) {
  const forwardKey = directedEdgeKey(wall.id, wall.startPointId, wall.endPointId);
  const reverseKey = directedEdgeKey(wall.id, wall.endPointId, wall.startPointId);
  return edgeContextMap.get(forwardKey) ?? edgeContextMap.get(reverseKey) ?? null;
}

/**
 * @param {Map<string, RoomEdgeContext>} edgeContextMap
 * @param {DesignerWall} wall
 * @param {string} pivotPointId
 * @returns {RoomEdgeContext | null}
 */
export function getWallEdgeContextAtPivot(edgeContextMap, wall, pivotPointId) {
  if (wall.startPointId === pivotPointId) {
    return (
      edgeContextMap.get(directedEdgeKey(wall.id, wall.startPointId, wall.endPointId)) ??
      edgeContextMap.get(directedEdgeKey(wall.id, wall.endPointId, wall.startPointId)) ??
      null
    );
  }
  if (wall.endPointId === pivotPointId) {
    return (
      edgeContextMap.get(directedEdgeKey(wall.id, wall.endPointId, wall.startPointId)) ??
      edgeContextMap.get(directedEdgeKey(wall.id, wall.startPointId, wall.endPointId)) ??
      null
    );
  }
  return resolveWallDirectedContext(wall, edgeContextMap);
}

/**
 * @param {DesignerGraph} graph
 * @returns {{
 *   edgeContextMap: Map<string, RoomEdgeContext>;
 *   wallEdgeLookup: Map<string, Array<{ roomId: string; edgeKey: string }>>;
 *   rooms: DetectedRoom[];
 *   partitionWallIds: Set<string>;
 * }}
 */
export function buildWallRoomEdgeContext(graph) {
  /** @type {Map<string, RoomEdgeContext>} */
  const edgeContextMap = new Map();
  /** @type {Map<string, Array<{ roomId: string; edgeKey: string; boundaryWallId: string }>>} */
  const wallEdgeLookup = new Map();
  const { rooms: detectedRooms, boundaryWallById } = buildRoomBoundaryModel(graph);
  const thicknessDirection = graph.wallSettings.thicknessDirection;
  const faces = detectedRooms.map((room) => ({
    pointIds: room.pointIds,
    polygon: room.polygon,
    areaSqM: room.areaSqM,
    wallIds: room.wallIds,
    boundaryEdges: room.boundaryEdges,
  }));

  for (const room of detectedRooms) {
    const signedArea = signedPolygonArea2D(room.polygon);
    for (const boundaryWallId of room.boundaryWallIds) {
      const bw = boundaryWallById.get(boundaryWallId);
      if (!bw) continue;
      const key = directedEdgeKey(bw.physicalWallId, bw.startPointId, bw.endPointId);
      const wall = graph.walls.find((item) => item.id === bw.physicalWallId);
      const edgeFollowsLoop = wall
        ? wall.startPointId === bw.startPointId && wall.endPointId === bw.endPointId
        : true;
      edgeContextMap.set(key, {
        roomId: room.id,
        signedArea,
        thicknessDirection,
        edgeFollowsLoop,
        boundaryWallId: bw.boundaryWallId,
      });
      if (!wallEdgeLookup.has(bw.physicalWallId)) wallEdgeLookup.set(bw.physicalWallId, []);
      wallEdgeLookup.get(bw.physicalWallId).push({
        roomId: room.id,
        edgeKey: key,
        boundaryWallId: bw.boundaryWallId,
      });
    }
  }

  return {
    edgeContextMap,
    wallEdgeLookup,
    rooms: faces,
    detectedRooms,
    boundaryWallById,
    partitionWallIds: getPartitionWallIds(faces),
  };
}

/**
 * @param {DesignerWall} wallA
 * @param {DesignerWall} wallB
 * @param {string} pivotId
 * @param {DetectedRoom[]} rooms
 * @returns {DetectedRoom | null}
 */
function findSharedRoomForWallPair(wallA, wallB, pivotId, rooms) {
  for (const room of rooms) {
    const edges = room.boundaryEdges || [];
    const hasA = edges.some(
      (edge) =>
        edge.wallId === wallA.id &&
        (edge.fromPointId === pivotId || edge.toPointId === pivotId),
    );
    const hasB = edges.some(
      (edge) =>
        edge.wallId === wallB.id &&
        (edge.fromPointId === pivotId || edge.toPointId === pivotId),
    );
    if (hasA && hasB) return room;
  }
  return null;
}

/**
 * @param {DesignerGraph} graph
 * @returns {Map<string, { signedArea: number; thicknessDirection: 'inside' | 'outside'; edgeFollowsLoop: boolean }>}
 */
export function buildWallRoomContext(graph) {
  const { edgeContextMap } = buildWallRoomEdgeContext(graph);
  /** @type {Map<string, { signedArea: number; thicknessDirection: 'inside' | 'outside'; edgeFollowsLoop: boolean }>} */
  const map = new Map();
  for (const wall of graph.walls) {
    const ctx = resolveWallDirectedContext(wall, edgeContextMap);
    if (ctx) {
      map.set(wall.id, {
        signedArea: ctx.signedArea,
        thicknessDirection: ctx.thicknessDirection,
        edgeFollowsLoop: ctx.edgeFollowsLoop,
      });
    }
  }
  return map;
}

/**
 * @param {Point} start
 * @param {Point} end
 * @param {{ signedArea: number; thicknessDirection: 'inside' | 'outside'; edgeFollowsLoop: boolean } | null} roomContext
 * @returns {Vec2}
 */
export function getWallOffsetNormal(start, end, roomContext) {
  const { left, right } = wallAxisFromPivotEdge(start, end);

  if (roomContext) {
    const interiorRight = roomContext.signedArea > 0;
    let interiorNormal = interiorRight ? right : left;
    if (!roomContext.edgeFollowsLoop) {
      interiorNormal = negateVec(interiorNormal);
    }
    if (roomContext.thicknessDirection === 'outside') {
      return negateVec(interiorNormal);
    }
    return interiorNormal;
  }

  return right;
}

/**
 * @param {Point} pivot
 * @param {string} pivotPointId
 * @param {DesignerWall} wall
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @param {1 | -1} [sideSign]
 * @param {BoundaryEdgeRef | null} [boundaryEdge]
 * @returns {{ dir: Vec2; normal: Vec2; halfThicknessPx: number; anchor: Point } | null}
 */
export function getWallOffsetLineAtPivot(
  pivot,
  pivotPointId,
  wall,
  graph,
  pixelsPerMeter,
  sideSign = 1,
  boundaryEdge = null,
) {
  const startPt = getPointById(/** @type {DesignerState} */ (graph), wall.startPointId);
  const endPt = getPointById(/** @type {DesignerState} */ (graph), wall.endPointId);
  if (!startPt || !endPt) return null;

  const wallStart = [startPt.x, startPt.y];
  const wallEnd = [endPt.x, endPt.y];
  const wallAxis = wallAxisFromPivotEdge(wallStart, wallEnd);
  if (wallAxis.length < 1e-6) return null;

  let awayPointId = wall.startPointId === pivotPointId ? wall.endPointId : wall.startPointId;
  if (boundaryEdge) {
    awayPointId =
      boundaryEdge.fromPointId === pivotPointId
        ? boundaryEdge.toPointId
        : boundaryEdge.fromPointId;
  }

  const awayPt = getPointById(/** @type {DesignerState} */ (graph), awayPointId);
  if (!awayPt) return null;

  const away = [awayPt.x, awayPt.y];
  const awayAxis = wallAxisFromPivotEdge(pivot, away);
  if (awayAxis.length < 1e-6) return null;

  const normal = getCenterlineWallNormal(wallStart, wallEnd);
  const thicknessM = wall.thicknessMeters ?? graph.wallSettings.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M;
  const halfThicknessPx = (thicknessM * pixelsPerMeter) / 2;
  const anchor = [
    pivot[0] + normal.x * halfThicknessPx * sideSign,
    pivot[1] + normal.y * halfThicknessPx * sideSign,
  ];

  return { dir: awayAxis.dir, normal, halfThicknessPx, anchor };
}

/**
 * @param {Point} anchor
 * @param {Vec2} dir
 * @param {Point} anchor2
 * @param {Vec2} dir2
 * @returns {Point | null}
 */
export function intersectOffsetLines(anchor, dir, anchor2, dir2) {
  const cross = dir.x * dir2.y - dir.y * dir2.x;
  if (Math.abs(cross) < PARALLEL_EPS) return null;
  const dx = anchor2[0] - anchor[0];
  const dy = anchor2[1] - anchor[1];
  const t = (dx * dir2.y - dy * dir2.x) / cross;
  return [anchor[0] + dir.x * t, anchor[1] + dir.y * t];
}

/**
 * @param {Point} pivot
 * @param {Point} join
 * @param {number} maxJoinDistance
 * @returns {boolean}
 */
function isJoinDistanceValid(pivot, join, maxJoinDistance) {
  return Math.hypot(join[0] - pivot[0], join[1] - pivot[1]) <= maxJoinDistance;
}

/**
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @param {Point} pivot
 * @param {string} pivotId
 * @param {DesignerWall} wallA
 * @param {DesignerWall} wallB
 * @param {DetectedRoom} room
 * @param {Map<string, JoinPair | null>} joinPoints
 * @param {Map<string, JoinPair>} roomJoinPoints
 * @param {Set<string>} bevelPivots
 * @param {boolean} storeAsGlobal
 * @param {Map<string, JoinPair>} wallPivotJoins
 * @param {string[]} wallsToStore
 * @param {BoundaryEdgeRef | null} [boundaryEdgeA]
 * @param {BoundaryEdgeRef | null} [boundaryEdgeB]
 * @returns {JoinPair | null}
 */
function computeJoinPairAtPivot(
  graph,
  pixelsPerMeter,
  pivot,
  pivotId,
  wallA,
  wallB,
  boundaryEdgeA = null,
  boundaryEdgeB = null,
) {
  /** @type {JoinPair | null} */
  let joinPair = null;

  for (const sideSign of /** @type {const} */ ([1, -1])) {
    const lineA = getWallOffsetLineAtPivot(
      pivot,
      pivotId,
      wallA,
      graph,
      pixelsPerMeter,
      sideSign,
      boundaryEdgeA,
    );
    const lineB = getWallOffsetLineAtPivot(
      pivot,
      pivotId,
      wallB,
      graph,
      pixelsPerMeter,
      sideSign,
      boundaryEdgeB,
    );
    if (!lineA || !lineB) return null;

    const maxJoinDistance = Math.max(lineA.halfThicknessPx * 8, 20);
    const join = intersectOffsetLines(lineA.anchor, lineA.dir, lineB.anchor, lineB.dir);
    if (!join || !Number.isFinite(join[0]) || !Number.isFinite(join[1])) return null;
    if (!isJoinDistanceValid(pivot, join, maxJoinDistance)) return null;

    if (!joinPair) joinPair = { outer: join, inner: join };
    if (sideSign === 1) joinPair.outer = join;
    else joinPair.inner = join;
  }

  return joinPair;
}

/**
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @param {Point} pivot
 * @param {string} pivotId
 * @param {DesignerWall} wallA
 * @param {DesignerWall} wallB
 * @param {DetectedRoom} room
 * @param {Map<string, JoinPair | null>} joinPoints
 * @param {Map<string, JoinPair>} roomJoinPoints
 * @param {Set<string>} bevelPivots
 * @param {boolean} storeAsGlobal
 * @param {Map<string, JoinPair>} wallPivotJoins
 * @param {string[]} wallsToStore
 * @param {BoundaryEdgeRef | null} [boundaryEdgeA]
 * @param {BoundaryEdgeRef | null} [boundaryEdgeB]
 * @param {string | null} [roomJoinKeySuffix]
 * @returns {boolean}
 */
function tryStorePivotJoin(
  graph,
  pixelsPerMeter,
  pivot,
  pivotId,
  wallA,
  wallB,
  room,
  joinPoints,
  roomJoinPoints,
  bevelPivots,
  storeAsGlobal,
  wallPivotJoins,
  wallsToStore,
  boundaryEdgeA = null,
  boundaryEdgeB = null,
  roomJoinKeySuffix = null,
) {
  const joinPair = computeJoinPairAtPivot(
    graph,
    pixelsPerMeter,
    pivot,
    pivotId,
    wallA,
    wallB,
    boundaryEdgeA,
    boundaryEdgeB,
  );
  if (!joinPair) return false;

  const polyA = buildPivotWallPolygonWithOffsets(
    wallA,
    graph,
    pixelsPerMeter,
    joinPoints,
    roomJoinPoints,
    bevelPivots,
    wallPivotJoins,
    pivotId,
    joinPair,
    null,
  );
  const polyB = buildPivotWallPolygonWithOffsets(
    wallB,
    graph,
    pixelsPerMeter,
    joinPoints,
    roomJoinPoints,
    bevelPivots,
    wallPivotJoins,
    pivotId,
    joinPair,
    null,
  );

  if (
    !polyA ||
    !polyB ||
    isPolygonSelfIntersecting(polyA) ||
    isPolygonSelfIntersecting(polyB) ||
    Math.abs(polygonArea(polyA)) < MIN_POLYGON_AREA ||
    Math.abs(polygonArea(polyB)) < MIN_POLYGON_AREA
  ) {
    return false;
  }

  if (storeAsGlobal) {
    joinPoints.set(pivotId, joinPair);
  } else if (room.wallIds.length > 0) {
    const suffix = roomJoinKeySuffix ? `:${roomJoinKeySuffix}` : '';
    roomJoinPoints.set(`${pivotId}:${getRoomIdForDetectedRoom(room)}${suffix}`, joinPair);
  }

  for (const wallId of wallsToStore) {
    wallPivotJoins.set(wallPivotJoinKey(pivotId, wallId), joinPair);
  }

  return true;
}

/**
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @param {Point} pivot
 * @param {string} pivotId
 * @param {import('./floorPlanDesignerBoundaryWalls.js').TJunctionInfo} tJunction
 * @param {Map<string, JoinPair | null>} joinPoints
 * @param {Map<string, JoinPair>} roomJoinPoints
 * @param {Set<string>} bevelPivots
 * @param {Map<string, JoinPair>} wallPivotJoins
 * @param {DetectedRoom[]} rooms
 * @returns {void}
 */
function processTJunctionJoins(
  graph,
  pixelsPerMeter,
  pivot,
  pivotId,
  tJunction,
  joinPoints,
  roomJoinPoints,
  bevelPivots,
  wallPivotJoins,
  rooms,
) {
  const wallStemA = graph.walls.find((wall) => wall.id === tJunction.stemWallIds[0]);
  const wallStemB = graph.walls.find((wall) => wall.id === tJunction.stemWallIds[1]);
  if (!wallStemA || !wallStemB) return;

  const sharedRoom =
    findSharedRoomForWallPair(wallStemA, wallStemB, pivotId, rooms) ?? {
      wallIds: [],
      boundaryEdges: [],
      polygon: [],
      pointIds: [],
      areaSqM: 0,
    };

  tryStorePivotJoin(
    graph,
    pixelsPerMeter,
    pivot,
    pivotId,
    wallStemA,
    wallStemB,
    sharedRoom,
    joinPoints,
    roomJoinPoints,
    bevelPivots,
    false,
    wallPivotJoins,
    tJunction.stemWallIds,
    null,
    null,
    'stem',
  );

  for (const cap of tJunction.capConnections) {
    const termWall = graph.walls.find((wall) => wall.id === cap.terminatorWallId);
    const capStemWall = graph.walls.find((wall) => wall.id === cap.capStemWallId);
    if (!termWall || !capStemWall) continue;

    const room =
      rooms.find((item) => getRoomIdForDetectedRoom(item) === cap.roomId) ?? {
        wallIds: [],
        boundaryEdges: [],
        polygon: [],
        pointIds: [],
        areaSqM: 0,
      };

    const termEdge =
      room.boundaryEdges?.find(
        (edge) => edge.wallId === cap.terminatorWallId && edge.toPointId === pivotId,
      ) ??
      room.boundaryEdges?.find(
        (edge) => edge.wallId === cap.terminatorWallId && edge.fromPointId === pivotId,
      ) ??
      null;

    const wallsToStore = [cap.terminatorWallId, cap.capStemWallId];
    const otherStemId = tJunction.stemWallIds.find((id) => id !== cap.capStemWallId);
    if (otherStemId) wallsToStore.push(otherStemId);

    tryStorePivotJoin(
      graph,
      pixelsPerMeter,
      pivot,
      pivotId,
      termWall,
      capStemWall,
      room,
      joinPoints,
      roomJoinPoints,
      bevelPivots,
      false,
      wallPivotJoins,
      wallsToStore,
      termEdge,
      cap.mirrorEdge,
      `cap:${cap.terminatorWallId}`,
    );

    if (otherStemId && otherStemId !== cap.capStemWallId) {
      const otherStemWall = graph.walls.find((wall) => wall.id === otherStemId);
      if (otherStemWall) {
        tryStorePivotJoin(
          graph,
          pixelsPerMeter,
          pivot,
          pivotId,
          termWall,
          capStemWall,
          room,
          joinPoints,
          roomJoinPoints,
          bevelPivots,
          false,
          wallPivotJoins,
          [otherStemId],
          termEdge,
          cap.forwardEdge,
          `cap-stem:${cap.terminatorWallId}`,
        );
      }
    }
  }
}

/**
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @returns {{
 *   joinPoints: Map<string, JoinPair | null>;
 *   roomJoinPoints: Map<string, JoinPair>;
 *   wallPivotJoins: Map<string, JoinPair>;
 *   bevelPivots: Set<string>;
 *   tJunctions: import('./floorPlanDesignerBoundaryWalls.js').TJunctionInfo[];
 *   mirroredBoundaryWalls: import('./floorPlanDesignerBoundaryWalls.js').MirroredBoundaryWall[];
 * }}
 */
export function buildOffsetJoinPoints(graph, pixelsPerMeter, edgeBundle = null) {
  /** @type {Map<string, JoinPair | null>} */
  const joinPoints = new Map();
  /** @type {Map<string, JoinPair>} */
  const roomJoinPoints = new Map();
  /** @type {Map<string, JoinPair>} */
  const wallPivotJoins = new Map();
  /** @type {Set<string>} */
  const bevelPivots = new Set();
  const adj = buildPivotAdjacency(graph);
  const bundle = edgeBundle ?? buildWallRoomEdgeContext(graph);
  const { edgeContextMap, rooms } = bundle;
  const { tJunctions, mirroredBoundaryWalls } = buildBoundaryWallBundle(adj, rooms, graph);
  /** @type {Set<string>} */
  const tJunctionPivotIds = new Set(tJunctions.map((item) => item.pivotId));

  for (const [pivotId, incidents] of adj.entries()) {
    const pivotPt = getPointById(/** @type {DesignerState} */ (graph), pivotId);
    if (!pivotPt) continue;
    const pivot = [pivotPt.x, pivotPt.y];

    if (tJunctionPivotIds.has(pivotId)) {
      const tJunction = getTJunctionAtPivot(pivotId, tJunctions);
      if (tJunction) {
        processTJunctionJoins(
          graph,
          pixelsPerMeter,
          pivot,
          pivotId,
          tJunction,
          joinPoints,
          roomJoinPoints,
          bevelPivots,
          wallPivotJoins,
          rooms,
        );
      }
      continue;
    }

    if (incidents.length !== 2) {
      if (incidents.length === 1) {
        bevelPivots.add(pivotId);
      }
      for (const room of rooms) {
        const edgesAtPivot = room.boundaryEdges.filter(
          (edge) => edge.fromPointId === pivotId || edge.toPointId === pivotId,
        );
        if (edgesAtPivot.length !== 2) continue;
        const wallA = graph.walls.find((wall) => wall.id === edgesAtPivot[0].wallId);
        const wallB = graph.walls.find((wall) => wall.id === edgesAtPivot[1].wallId);
        if (!wallA || !wallB) continue;
        tryStorePivotJoin(
          graph,
          pixelsPerMeter,
          pivot,
          pivotId,
          wallA,
          wallB,
          room,
          joinPoints,
          roomJoinPoints,
          bevelPivots,
          false,
          wallPivotJoins,
          [wallA.id, wallB.id],
        );
      }
      continue;
    }

    const wallA = graph.walls.find((w) => w.id === incidents[0].wallId);
    const wallB = graph.walls.find((w) => w.id === incidents[1].wallId);
    if (!wallA || !wallB) {
      bevelPivots.add(pivotId);
      continue;
    }

    const sharedRoom = findSharedRoomForWallPair(wallA, wallB, pivotId, rooms);
    const joined = tryStorePivotJoin(
      graph,
      pixelsPerMeter,
      pivot,
      pivotId,
      wallA,
      wallB,
      sharedRoom ?? {
        wallIds: [],
        boundaryEdges: [],
        polygon: [],
        pointIds: [],
        areaSqM: 0,
      },
      joinPoints,
      roomJoinPoints,
      bevelPivots,
      true,
      wallPivotJoins,
      [wallA.id, wallB.id],
    );
    if (!joined) {
      bevelPivots.add(pivotId);
    }
  }

  return {
    joinPoints,
    roomJoinPoints,
    wallPivotJoins,
    bevelPivots,
    edgeContextMap,
    rooms,
    partitionWallIds: bundle.partitionWallIds,
    tJunctions,
    mirroredBoundaryWalls,
  };
}

/**
 * @param {Point} pivot
 * @param {Vec2} normal
 * @param {number} thicknessPx
 * @returns {Point}
 */
export function simpleOffsetPoint(pivot, normal, thicknessPx) {
  return [pivot[0] + normal.x * thicknessPx, pivot[1] + normal.y * thicknessPx];
}

/**
 * @param {Point} outerA
 * @param {Point} outerB
 * @param {Point} innerA
 * @param {Point} innerB
 * @returns {Point[]}
 */
export function buildCenterlineWallPolygon(outerA, outerB, innerA, innerB) {
  return [outerA, outerB, innerB, innerA];
}

/** @deprecated Use buildCenterlineWallPolygon */
export function buildBasicPivotPolygon(A, B, offsetA, offsetB) {
  return [A, B, offsetB, offsetA];
}

/**
 * @param {DesignerWall} wall
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @param {Map<string, JoinPair | null>} joinPoints
 * @param {Map<string, JoinPair>} roomJoinPoints
 * @param {Set<string>} bevelPivots
 * @param {string} overridePivotId
 * @param {JoinPair | null} overrideJoinPair
 * @param {string | null} skipPivotId
 * @returns {Point[] | null}
 */
function buildPivotWallPolygonWithOffsets(
  wall,
  graph,
  pixelsPerMeter,
  joinPoints,
  roomJoinPoints,
  bevelPivots,
  wallPivotJoins,
  overridePivotId,
  overrideJoinPair,
  skipPivotId,
) {
  const startPt = getPointById(/** @type {DesignerState} */ (graph), wall.startPointId);
  const endPt = getPointById(/** @type {DesignerState} */ (graph), wall.endPointId);
  if (!startPt || !endPt) return null;

  const A = [startPt.x, startPt.y];
  const B = [endPt.x, endPt.y];
  const normal = getCenterlineWallNormal(A, B);
  const thicknessM = wall.thicknessMeters ?? graph.wallSettings.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M;
  const halfThicknessPx = (thicknessM * pixelsPerMeter) / 2;

  const resolveSide = (pivot, pivotId, side) => {
    const sign = side === 'outer' ? 1 : -1;
    if (overridePivotId === pivotId && overrideJoinPair) {
      return side === 'outer' ? overrideJoinPair.outer : overrideJoinPair.inner;
    }
    if (skipPivotId === pivotId) {
      return simpleOffsetPoint(pivot, normal, halfThicknessPx * sign);
    }
    const joinPair = getJoinPairForWallAtPivot(
      pivotId,
      wall.id,
      joinPoints,
      roomJoinPoints,
      wallPivotJoins,
    );
    if (joinPair && !bevelPivots.has(pivotId)) {
      return side === 'outer' ? joinPair.outer : joinPair.inner;
    }
    if (bevelPivots.has(pivotId)) {
      return simpleOffsetPoint(pivot, normal, halfThicknessPx * sign);
    }
    return simpleOffsetPoint(pivot, normal, halfThicknessPx * sign);
  };

  const outerA = resolveSide(A, wall.startPointId, 'outer');
  const outerB = resolveSide(B, wall.endPointId, 'outer');
  const innerA = resolveSide(A, wall.startPointId, 'inner');
  const innerB = resolveSide(B, wall.endPointId, 'inner');
  return buildCenterlineWallPolygon(outerA, outerB, innerA, innerB);
}

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} pixelsPerMeter
 * @returns {boolean}
 */
export function isPivotEdgeLongEnough(start, end, pixelsPerMeter) {
  const ppm = pixelsPerMeter > 0 ? pixelsPerMeter : 100;
  const lenPx = Math.hypot(end[0] - start[0], end[1] - start[1]);
  return lenPx >= MIN_WALL_LENGTH_METERS * ppm;
}

/**
 * @param {Point[]} polygon
 * @param {string} [wallId]
 * @returns {Point[] | null}
 */
export function validatePivotWallPolygon(polygon, wallId = '') {
  if (!arePolygonPointsFinite(polygon)) {
    console.warn('[2D Designer] skipped invalid wall polygon', { wallId, reason: 'non-finite' });
    return null;
  }
  if (Math.abs(polygonArea(polygon)) < MIN_POLYGON_AREA) {
    return null;
  }
  if (isPolygonSelfIntersecting(polygon)) {
    console.warn('[2D Designer] skipped invalid wall polygon', { wallId, reason: 'self-intersect' });
    return null;
  }
  return polygon;
}

/**
 * @param {DesignerWall} wall
 * @param {DesignerGraph} graph
 * @param {number} pixelsPerMeter
 * @param {{ joinPoints: Map<string, JoinPair | null>; roomJoinPoints: Map<string, JoinPair>; wallPivotJoins: Map<string, JoinPair>; bevelPivots: Set<string> } | null} [joinCache]
 * @returns {Point[] | null}
 */
export function buildPivotWallPolygon2D(wall, graph, pixelsPerMeter, joinCache = null) {
  const renderModel =
    joinCache && 'wallBodies' in joinCache
      ? /** @type {import('./floorPlanDesignerDynamicWallEnds.js').DynamicWallRenderModel} */ (joinCache)
      : buildDynamicWallRenderModel(graph, pixelsPerMeter);
  return renderModel.wallBodies.get(wall.id) ?? null;
}

/**
 * @typedef {Point[]} WallPolygonEntry
 */

/**
 * @param {DesignerState | DesignerGraph} input
 * @param {number} pixelsPerMeter
 * @returns {Map<string, WallPolygonEntry>}
 */
export function buildAllPivotWallPolygons2D(input, pixelsPerMeter) {
  const graph = toDesignerGraph(input);
  return buildDynamicWallRenderModel(graph, pixelsPerMeter).wallBodies;
}

/**
 * @param {DesignerGraph} graph
 * @param {string} wallId
 * @param {number} t0
 * @param {number} t1
 * @param {number} pixelsPerMeter
 * @param {{ joinPoints: Map<string, JoinPair | null>; roomJoinPoints: Map<string, JoinPair>; wallPivotJoins: Map<string, JoinPair>; bevelPivots: Set<string> } | null} [joinCache]
 * @returns {Point[] | null}
 */
export function buildPivotWallPolygonForSegment(
  graph,
  wallId,
  t0,
  t1,
  pixelsPerMeter,
  joinCache = null,
) {
  const renderModel =
    joinCache && 'wallBodies' in joinCache
      ? /** @type {import('./floorPlanDesignerDynamicWallEnds.js').DynamicWallRenderModel} */ (joinCache)
      : null;
  return buildWallBodySegmentPolygon(graph, wallId, t0, t1, pixelsPerMeter, renderModel);
}

/**
 * Build a designer graph from manual-floor-plan analysis JSON.
 * @param {Record<string, unknown>} analysisJson
 * @returns {DesignerGraph | null}
 */
export function designerGraphFromAnalysisJson(analysisJson) {
  const rawPoints = Array.isArray(analysisJson?.points) ? analysisJson.points : [];
  const rawWalls = Array.isArray(analysisJson?.walls) ? analysisJson.walls : [];
  if (rawPoints.length === 0 || rawWalls.length === 0) return null;

  /** @type {DesignerPoint[]} */
  const points = rawPoints
    .map((p, index) => {
      if (!p || typeof p !== 'object') return null;
      const rec = /** @type {Record<string, unknown>} */ (p);
      const id = typeof rec.id === 'string' ? rec.id : `p_${index + 1}`;
      const x = Number(rec.x);
      const y = Number(rec.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      return { id, x, y };
    })
    .filter(Boolean);

  /** @type {DesignerWall[]} */
  const walls = rawWalls
    .map((w, index) => {
      if (!w || typeof w !== 'object') return null;
      const rec = /** @type {Record<string, unknown>} */ (w);
      const id = typeof rec.id === 'string' ? rec.id : `w_${index + 1}`;
      const startPointId = typeof rec.startPointId === 'string' ? rec.startPointId : '';
      const endPointId = typeof rec.endPointId === 'string' ? rec.endPointId : '';
      if (!startPointId || !endPointId) return null;
      const thicknessMeters = Number(rec.thicknessMeters);
      const color = typeof rec.color === 'string' ? rec.color : '#5f6b73';
      return {
        id,
        startPointId,
        endPointId,
        thicknessMeters: Number.isFinite(thicknessMeters) ? thicknessMeters : DEFAULT_WALL_THICKNESS_M,
        color,
      };
    })
    .filter(Boolean);

  if (points.length === 0 || walls.length === 0) return null;

  const wallSettings = normalizeWallSettings(
    analysisJson?.wallSettings && typeof analysisJson.wallSettings === 'object'
      ? /** @type {Partial<WallSettings>} */ (analysisJson.wallSettings)
      : null,
  );

  return { points, walls, wallSettings };
}
